import { yupResolver } from '@hookform/resolvers/yup';
import { POLL_TIME_TEN_SECONDS } from 'constants/constants';
import {
  PAGE_DEFAULT,
  QUERY_DEFAULT,
  SORT_ATTRIBUTE_CONTEXT_LIBRARY_DEFAULT,
  SORT_ORDER_DEFAULT,
  TAGS_ID_DEFAULT
} from 'constants/defaults';
import {
  ContextLibraryTagType,
  ContextLibraryUploadedFileSortAttributeEnum,
  ContextLibraryUploadedFileTypePage,
  SortOrderEnum
} from 'gql/graphql';
import { useTranslations } from 'next-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import { useContextLibraryData } from 'providers/ContextLibraryProviders/useContextLibraryData';
import { useContextLibraryTags } from 'providers/ContextLibraryProviders/useContextLibraryTags';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { KeysEnum } from 'types/KeysEnum';
import { Option } from 'types/Option';
import { TScheme } from 'types/TScheme';
import { useDebounce } from 'use-debounce';
import { extractParam } from 'utils/paramsUtil';
import * as yup from 'yup';

export interface FiltersContextLibraryForm {
  [KeysEnum.SortAttribute]: ContextLibraryUploadedFileSortAttributeEnum;
  [KeysEnum.SortOrder]: SortOrderEnum;
  [KeysEnum.TagId]: string;
  [KeysEnum.LanguagePair]: string;
  [KeysEnum.Query]: string | undefined;
  [KeysEnum.Page]: number;
}

interface IUseSearchParamsContextLibraryOut {
  control: Control<FiltersContextLibraryForm>;
  deferredSearchQuery?: string;
  searchQuery?: string;
  data?: ContextLibraryUploadedFileTypePage;
  isLoading: boolean;
  isPending: boolean;
  isLoadingTags: boolean;
  isPendingTags: boolean;
  refetch: () => Promise<void>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  optionsSortAttribute: Option[];
  optionsSortOrder: Option[];
  optionsFilterTags: Option[];
  dataTags?: Array<ContextLibraryTagType>;
  defaultTags: string[];
  handleClear: () => void;
}

export default function useSearchParamsContextLibrary(): IUseSearchParamsContextLibraryOut {
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [page, setPage] = useState(PAGE_DEFAULT);

  const pageInitial = extractParam(searchParams, KeysEnum.Page, PAGE_DEFAULT);
  const queryInitial = extractParam(searchParams, KeysEnum.Query, QUERY_DEFAULT);
  const sortAttributeInitial = extractParam(
    searchParams,
    KeysEnum.SortAttribute,
    SORT_ATTRIBUTE_CONTEXT_LIBRARY_DEFAULT
  );
  const sortOrderInitial = extractParam(searchParams, KeysEnum.SortOrder, SORT_ORDER_DEFAULT);
  const tagIdInitial = extractParam(searchParams, KeysEnum.TagId, TAGS_ID_DEFAULT);

  const isList = pathname.includes('/smart-tasks/context-library/list');

  const schema = yup.object().shape({} as TScheme<FiltersContextLibraryForm>);
  const {
    control,
    setValue,
    formState: { isDirty }
  } = useForm<FiltersContextLibraryForm>({
    resolver: yupResolver(schema)
  });

  const query = useWatch({
    control,
    name: KeysEnum.Query,
    defaultValue: QUERY_DEFAULT
  });

  const sortAttribute = useWatch<FiltersContextLibraryForm, typeof KeysEnum.SortAttribute>({
    control,
    name: KeysEnum.SortAttribute,
    defaultValue: SORT_ATTRIBUTE_CONTEXT_LIBRARY_DEFAULT
  });

  const sortOrder = useWatch<FiltersContextLibraryForm, typeof KeysEnum.SortOrder>({
    control,
    name: KeysEnum.SortOrder,
    defaultValue: SORT_ORDER_DEFAULT
  });

  const tagId = useWatch<FiltersContextLibraryForm, typeof KeysEnum.TagId>({
    control,
    name: KeysEnum.TagId,
    defaultValue: TAGS_ID_DEFAULT
  });

  const [deferredQuery] = useDebounce(query, 1000);

  const {
    data: dataTags,
    isLoading: isLoadingTags,
    isPending: isPendingTags,
    refetch: refetchTags
  } = useContextLibraryTags();

  const {
    data: data,
    isLoading,
    isPending,
    refetch: refetchData
  } = useContextLibraryData(
    {
      page,
      query: deferredQuery,
      sortAttribute,
      sortOrder,
      tagId
    },
    {
      enabled: deferredQuery === query,
      refetchInterval: POLL_TIME_TEN_SECONDS
    }
  );

  const refetch = async () => {
    await refetchData();
    await refetchTags();
  };

  const optionsSortAttribute = [
    {
      label: t(
        `context_library.search.filters.sort_attributes.${ContextLibraryUploadedFileSortAttributeEnum.Filename}`
      ),
      value: ContextLibraryUploadedFileSortAttributeEnum.Filename
    },
    {
      label: t(
        `context_library.search.filters.sort_attributes.${ContextLibraryUploadedFileSortAttributeEnum.Filesize}`
      ),
      value: ContextLibraryUploadedFileSortAttributeEnum.Filesize
    },
    {
      label: t(
        `context_library.search.filters.sort_attributes.${ContextLibraryUploadedFileSortAttributeEnum.CreatedAt}`
      ),
      value: ContextLibraryUploadedFileSortAttributeEnum.CreatedAt
    }
  ];

  const optionsSortOrder = [
    {
      label: t(`context_library.search.filters.sort_order.${SortOrderEnum.Asc}`),
      value: SortOrderEnum.Asc
    },
    {
      label: t(`context_library.search.filters.sort_order.${SortOrderEnum.Desc}`),
      value: SortOrderEnum.Desc
    }
  ];

  const optionsFilterTags = useMemo(
    () => [
      {
        label: t('context_library.search.filters.filter_tags.all'),
        value: undefined
      },
      ...(dataTags?.map((tags) => ({
        label: tags.name,
        value: tags.id
      })) ?? [])
    ],
    [dataTags]
  );

  const defaultTags = useMemo(() => dataTags?.map((tags) => tags.name) ?? [], [dataTags, data]);

  const handleClear = useCallback(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      url.searchParams.delete(KeysEnum.TagId);
      url.searchParams.delete(KeysEnum.Page);

      setPage(pageInitial);
      setValue(KeysEnum.TagId, tagIdInitial);

      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  useEffect(() => {
    setPage(pageInitial);
    setValue(KeysEnum.Query, queryInitial);
    setValue(KeysEnum.SortAttribute, sortAttributeInitial);
    setValue(KeysEnum.SortOrder, sortOrderInitial);
    setValue(KeysEnum.TagId, tagIdInitial);
  }, [pageInitial, sortAttributeInitial, sortOrderInitial, queryInitial, tagIdInitial, setValue]);

  useEffect(() => {
    if (typeof window !== 'undefined' && isList) {
      const url = new URL(window.location.href);

      if (deferredQuery) url.searchParams.set(KeysEnum.Query, JSON.stringify(deferredQuery));
      else url.searchParams.delete(KeysEnum.Query);

      if (sortAttribute)
        url.searchParams.set(KeysEnum.SortAttribute, JSON.stringify(sortAttribute));
      else url.searchParams.delete(KeysEnum.SortAttribute);

      if (sortOrder) url.searchParams.set(KeysEnum.SortOrder, JSON.stringify(sortOrder));
      else url.searchParams.delete(KeysEnum.SortOrder);

      if (tagId) url.searchParams.set(KeysEnum.TagId, JSON.stringify(tagId));
      else url.searchParams.delete(KeysEnum.TagId);

      url.searchParams.delete(KeysEnum.Page);
      setPage(
        [
          queryInitial !== deferredQuery,
          sortAttributeInitial !== sortAttribute,
          sortOrderInitial !== sortOrder,
          tagIdInitial !== tagId
        ].some(Boolean) && isDirty
          ? PAGE_DEFAULT
          : pageInitial
      );

      window.history.replaceState({}, '', url.toString());
    }
  }, [
    deferredQuery,
    pageInitial,
    sortAttribute,
    sortOrder,
    tagId,
    isList,
    queryInitial,
    sortAttributeInitial,
    sortOrderInitial,
    tagIdInitial,
    isDirty
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined' && isList) {
      const url = new URL(window.location.href);

      if (page > PAGE_DEFAULT) url.searchParams.set(KeysEnum.Page, JSON.stringify(page));
      else url.searchParams.delete(KeysEnum.Page);

      window.history.replaceState({}, '', url.toString());
    }
  }, [page, pathname, isList]);

  return {
    control,
    deferredSearchQuery: deferredQuery,
    searchQuery: query,
    data,
    isLoading,
    isPending,
    isLoadingTags,
    isPendingTags,
    refetch,
    page,
    setPage,
    optionsSortAttribute,
    optionsSortOrder,
    optionsFilterTags,
    dataTags,
    defaultTags,
    handleClear
  };
}
